import { useCallback, useEffect, useState } from 'react';
import { __ } from '@/hub-shared/utils/i18n';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import type { MissionSummary } from '../types/missionPortal';

type UseMissionSummaryArgs = {
  hubBaseUrl: string;
  nonce: string;
  missionId: number;
};

type UseMissionSummaryResult = {
  summary: MissionSummary | null;
  loading: boolean;
  error: string;
  setError: (message: string) => void;
  refreshSummary: () => Promise<void>;
  setSummary: (summary: MissionSummary | null) => void;
};

/**
 * Loads and refreshes the Hub mission summary (`GET missions/{id}`).
 */
export const useMissionSummary = ({
  hubBaseUrl,
  nonce,
  missionId,
}: UseMissionSummaryArgs): UseMissionSummaryResult => {
  const [summary, setSummary] = useState<MissionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refreshSummary = useCallback(async (): Promise<void> => {
    const response = await fetchWithNonce(`${hubBaseUrl}missions/${missionId}`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'X-WP-Nonce': nonce,
      },
    });

    if (!response.ok) {
      setError(await parseApiError(response));
      return;
    }

    const payload = (await response.json()) as { data?: MissionSummary };
    setSummary(payload.data ?? null);
  }, [hubBaseUrl, missionId, nonce]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetchWithNonce(`${hubBaseUrl}missions/${missionId}`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': nonce,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          if (active) {
            setError(await parseApiError(response));
          }
          return;
        }

        const payload = (await response.json()) as { data?: MissionSummary };
        if (active) {
          setSummary(payload.data ?? null);
        }
      } catch {
        if (active) {
          setError(__('Impossible de charger la mission.'));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      active = false;
      controller.abort();
    };
  }, [hubBaseUrl, missionId, nonce]);

  return {
    summary,
    loading,
    error,
    setError,
    refreshSummary,
    setSummary,
  };
};
